import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, MapPin, Clock, Users, ChevronLeft, ChevronRight, Calendar as CalendarIcon, Bell, X, HandHelping, DoorOpen, Trash2, Check, UserCheck, UserX } from "lucide-react";
import { pickColor } from "@/lib/palette";
import { useAuth } from "@/context/AuthContext";
import { pushSupported, pushPermission, getCurrentSubscription, subscribeToPush } from "@/lib/push";
import { modeOf } from "@/components/ModePicker";

function toISO(d) {
  return d.toISOString().slice(0, 10);
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function startOfWeek(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // Monday = 0
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
}
const DOW = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [selected, setSelected] = useState(() => toISO(new Date()));
  const [groupInfo, setGroupInfo] = useState(null); // {id, name, owner_id}
  const [members, setMembers] = useState([]);
  const [pendingMembers, setPendingMembers] = useState([]);

  const loadGroup = async () => {
    try {
      const { data } = await api.get("/groups/me");
      if (data.group) {
        setGroupInfo(data.group);
        setMembers(data.members || []);
        // If owner, load pending
        if (user && data.group.owner_id === user.id) {
          try {
            const p = await api.get(`/groups/${data.group.id}/pending`);
            setPendingMembers(p.data.pending || []);
          } catch { /* ignore */ }
        }
      }
    } catch { /* ignore */ }
  };

  useEffect(() => {
    loadGroup();
    // eslint-disable-next-line
  }, [user?.id]);

  const isOwner = !!(groupInfo && user && groupInfo.owner_id === user.id);

  const approveMember = async (uid) => {
    try {
      await api.post(`/groups/${groupInfo.id}/approve/${uid}`);
      toast.success("Membro approvato");
      await loadGroup();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const rejectMember = async (uid) => {
    if (!window.confirm("Vuoi rifiutare questa richiesta?")) return;
    try {
      await api.post(`/groups/${groupInfo.id}/reject/${uid}`);
      toast.success("Richiesta rifiutata");
      await loadGroup();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const leaveGroup = async () => {
    if (!window.confirm("Vuoi davvero uscire da questo gruppo?")) return;
    try {
      const { data: res } = await api.post("/groups/leave");
      await refresh();
      if (!res.active_group_id) navigate("/onboarding");
      else navigate("/home");
      toast.success("Uscito dal gruppo");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteGroup = async () => {
    if (!groupInfo) return;
    const name = groupInfo.name;
    if (!window.confirm(`Stai per CANCELLARE il gruppo "${name}". Verranno eliminate TUTTE le corse e i membri verranno rimossi. Operazione irreversibile.\n\nProcedere?`)) return;
    const typed = window.prompt(`Per conferma scrivi il nome del gruppo: ${name}`);
    if (typed !== name) {
      toast.error("Nome non corrispondente. Cancellazione annullata.");
      return;
    }
    try {
      await api.delete(`/groups/${groupInfo.id}`);
      await refresh();
      toast.success("Gruppo cancellato");
      const { data: list } = await api.get("/groups");
      navigate(list.groups && list.groups.length ? "/home" : "/onboarding");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  const load = async () => {
    setLoading(true);
    try {
      const from = toISO(weekStart);
      const to = toISO(addDays(weekStart, 6));
      const { data } = await api.get("/rides", { params: { date_from: from, date_to: to } });
      setRides(data.rides);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [weekStart]);

  const ridesByDay = useMemo(() => {
    const m = {};
    for (const r of rides) {
      (m[r.date] = m[r.date] || []).push(r);
    }
    return m;
  }, [rides]);

  const dayRides = ridesByDay[selected] || [];
  const todayISO = toISO(new Date());

  // Push notification banner (shown only if dismissable and permission is default)
  const [pushBannerVisible, setPushBannerVisible] = useState(false);
  useEffect(() => {
    (async () => {
      if (!pushSupported()) return;
      if (localStorage.getItem("sq_push_dismissed") === "1") return;
      const perm = pushPermission();
      if (perm !== "default") return;
      const sub = await getCurrentSubscription();
      if (!sub) setPushBannerVisible(true);
    })();
  }, []);

  const enablePushFromBanner = async () => {
    try {
      await subscribeToPush();
      toast.success("Notifiche attivate!");
      setPushBannerVisible(false);
    } catch (e) {
      toast.error(e.message || "Errore notifiche");
    }
  };
  const dismissBanner = () => {
    localStorage.setItem("sq_push_dismissed", "1");
    setPushBannerVisible(false);
  };

  return (
    <div className="space-y-6 anim-slide-up" data-testid="dashboard-page">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-[#8A91A8] mb-1">Ciao {user?.name?.split(" ")[0]}</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[#1A1D29]">Le corse del gruppo</h1>
        </div>
        <Link to="/rides/new" className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-[#1A2A6C] text-white font-semibold shadow-[0_6px_18px_rgba(26,42,108,0.32)] hover:bg-[#14205A] transition-all" data-testid="dashboard-new-ride-button">
          <Plus className="w-4 h-4" /> Nuova corsa
        </Link>
      </div>

      {pushBannerVisible && (
        <div className="brut-card p-4 flex items-start gap-3" data-testid="push-banner">
          <div className="w-10 h-10 grid place-items-center rounded-xl bg-[#E5E9F5] text-[#1A2A6C] shrink-0">
            <Bell className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-[#1A1D29] leading-tight">Attiva le notifiche</p>
            <p className="text-sm text-[#8A91A8] leading-snug">Ricevi un avviso quando un genitore propone una corsa o prenota un posto.</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={enablePushFromBanner} className="px-3 py-2 rounded-xl bg-[#1A2A6C] text-white text-sm font-semibold shadow-[0_4px_12px_rgba(26,42,108,0.32)] hover:bg-[#14205A]" data-testid="push-banner-enable">
              Attiva
            </button>
            <button onClick={dismissBanner} aria-label="Chiudi" className="w-9 h-9 grid place-items-center rounded-xl text-[#8A91A8] hover:bg-[#F5F6FA]" data-testid="push-banner-dismiss">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Week strip */}
      <div className="brut-card p-4 sm:p-5">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => setWeekStart(addDays(weekStart, -7))} className="w-9 h-9 grid place-items-center rounded-xl bg-[#F5F6FA] text-[#1A2A6C] hover:bg-[#E5E9F5]" data-testid="week-prev">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="font-heading font-bold text-lg flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-[#1A2A6C]" />
            {MONTHS[weekStart.getMonth()]} {weekStart.getFullYear()}
          </div>
          <button onClick={() => setWeekStart(addDays(weekStart, 7))} className="w-9 h-9 grid place-items-center rounded-xl bg-[#F5F6FA] text-[#1A2A6C] hover:bg-[#E5E9F5]" data-testid="week-next">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="grid grid-cols-7 gap-2">
          {weekDays.map((d, i) => {
            const iso = toISO(d);
            const isSel = iso === selected;
            const isToday = iso === todayISO;
            const count = (ridesByDay[iso] || []).length;
            return (
              <button
                key={iso}
                onClick={() => setSelected(iso)}
                className={`relative flex flex-col items-center justify-center py-3 rounded-2xl transition-all ${isSel ? "bg-[#1A2A6C] text-white shadow-[0_6px_16px_rgba(26,42,108,0.32)]" : isToday ? "bg-[#D4F1ED] text-[#26B5A8] ring-1 ring-[#26B5A8]/40" : "bg-white text-[#1A1D29] hover:bg-[#F5F6FA] border border-[#ECEEF5]"}`}
                data-testid={`day-cell-${iso}`}
              >
                <span className={`text-[10px] font-semibold uppercase tracking-widest ${isSel ? "text-white/80" : "text-[#8A91A8]"}`}>{DOW[i]}</span>
                <span className="font-heading text-xl font-bold">{d.getDate()}</span>
                {count > 0 && (
                  <span className={`absolute -top-1 -right-1 min-w-[20px] h-5 px-1 grid place-items-center rounded-full text-[10px] font-bold ${isSel ? "bg-white text-[#1A2A6C]" : "bg-[#1A2A6C] text-white"}`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected day rides */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-xl sm:text-2xl font-bold tracking-tight">
            {(() => {
              const d = new Date(selected);
              return `${DOW[(d.getDay() + 6) % 7]} ${d.getDate()} ${MONTHS[d.getMonth()].toLowerCase()}`;
            })()}
          </h2>
          <span className="brut-chip">{dayRides.length} {dayRides.length === 1 ? "corsa" : "corse"}</span>
        </div>

        {loading ? (
          <div className="brut-card p-8 text-center font-bold">Caricamento…</div>
        ) : dayRides.length === 0 ? (
          <div className="brut-card p-8 text-center" data-testid="dashboard-empty">
            <div className="w-14 h-14 mx-auto mb-3 grid place-items-center rounded-2xl bg-[#E5E9F5] text-[#1A2A6C]">
              <CalendarIcon className="w-6 h-6" />
            </div>
            <p className="font-heading text-xl font-bold mb-1">Nessuna corsa per questo giorno</p>
            <p className="text-[#8A91A8] mb-5">Sei il primo? Proponi una corsa al gruppo.</p>
            <Link to="/rides/new" className="brut-btn" data-testid="dashboard-empty-new-ride">
              <Plus className="w-4 h-4" /> Crea corsa
            </Link>
          </div>
        ) : (
          <div className="grid gap-3">
            {dayRides.map((r) => {
              const isRequest = r.type === "request";
              const unclaimed = isRequest && !r.driver_id;
              return (
              <Link to={`/rides/${r.id}`} key={r.id} className={`brut-card brut-card-hover p-5 block ${unclaimed ? "border-l-4 border-l-[#FF9E1B]" : ""}`} data-testid={`ride-card-${r.id}`}>
                <div className="flex items-start gap-4">
                  <div className={`shrink-0 w-16 h-16 rounded-2xl grid place-items-center ${unclaimed ? "bg-[#FFF5DC]" : "bg-[#E5E9F5]"}`}>
                    <div className="text-center leading-none">
                      <div className={`font-heading font-bold text-lg ${unclaimed ? "text-[#FF9E1B]" : "text-[#1A2A6C]"}`}>{r.time}</div>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <MapPin className={`w-4 h-4 shrink-0 ${unclaimed ? "text-[#FF9E1B]" : "text-[#1A2A6C]"}`} />
                      <h3 className="font-heading font-bold text-lg truncate">{r.destination}</h3>
                      {(() => {
                        const m = modeOf(r.mode);
                        const ModeIcon = m.icon;
                        return (
                          <span className="brut-chip" style={{ backgroundColor: `${m.color}20`, color: m.color }} data-testid={`ride-mode-${r.id}`}>
                            <ModeIcon className="w-3 h-3" /> {m.label}
                          </span>
                        );
                      })()}
                      {isRequest && (
                        <span className={`brut-chip ${unclaimed ? "bg-[#FFE9C2] text-[#FF9E1B]" : "bg-[#D4F1ED] text-[#26B5A8]"}`}>
                          {unclaimed ? "Cerca corsa" : "Accettata"}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-[#8A91A8] mb-2">
                      {isRequest && unclaimed ? (
                        <>Richiesto da <span className="font-semibold text-[#1A1D29]">{r.requester_name}</span></>
                      ) : (
                        <>Guida <span className="font-semibold text-[#1A1D29]">{r.driver_name}</span></>
                      )}
                      {r.origin && <> · da {r.origin}</>}
                    </p>
                    <div className="flex items-center gap-2 flex-wrap">
                      {unclaimed ? (
                        <span className="brut-chip bg-[#FFE9C2] text-[#FF9E1B]">
                          <HandHelping className="w-3 h-3" /> Offriti come guidatore
                        </span>
                      ) : (
                        <span className={`brut-chip ${r.seats_left === 0 ? "bg-[#FFE3E3] text-[#FF4D5E]" : "bg-[#D4F1ED] text-[#26B5A8]"}`}>
                          <Users className="w-3 h-3" /> {r.seats_left}/{r.seats} {r.seats_left === 0 ? "esauriti" : "liberi"}
                        </span>
                      )}
                      <span className="brut-chip bg-[#F5F6FA] text-[#8A91A8]">
                        <Clock className="w-3 h-3" /> {r.time}
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* Pending requests (owner only) */}
      {isOwner && pendingMembers.length > 0 && (
        <div className="brut-card p-5 mt-4 border-l-4 border-l-[#FF9E1B]" data-testid="dashboard-pending-section">
          <div className="flex items-center justify-between gap-2 mb-3">
            <h2 className="font-heading text-lg font-bold flex items-center gap-2">
              <Bell className="w-4 h-4 text-[#FF9E1B]" /> Richieste di ingresso ({pendingMembers.length})
            </h2>
          </div>
          <ul className="space-y-2" data-testid="dashboard-pending-list">
            {pendingMembers.map((p) => {
              const c = pickColor(p.id);
              return (
                <li key={p.id} className="flex items-center gap-3 rounded-2xl bg-[#FFF5DC] p-3" data-testid={`pending-row-${p.id}`}>
                  <div className="w-10 h-10 grid place-items-center rounded-full font-heading font-bold shrink-0" style={{ backgroundColor: c.bg, color: c.text }}>
                    {p.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold leading-tight truncate">{p.name}</p>
                    <p className="text-xs text-[#8A91A8] truncate">{p.email}</p>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <button onClick={() => approveMember(p.id)} className="w-9 h-9 grid place-items-center rounded-xl bg-[#26B5A8] text-white hover:bg-[#1F9D91]" data-testid={`pending-approve-${p.id}`} title="Approva">
                      <UserCheck className="w-4 h-4" />
                    </button>
                    <button onClick={() => rejectMember(p.id)} className="w-9 h-9 grid place-items-center rounded-xl bg-white border border-[#FFD1D6] text-[#FF4D5E] hover:bg-[#FFE3E3]" data-testid={`pending-reject-${p.id}`} title="Rifiuta">
                      <UserX className="w-4 h-4" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Members of the active group */}
      {groupInfo && members.length > 0 && (
        <div className="brut-card p-5 mt-4" data-testid="dashboard-members-section">
          <h2 className="font-heading text-lg font-bold mb-3 flex items-center gap-2">
            <Users className="w-4 h-4 text-[#1A2A6C]" /> Membri di "{groupInfo.name}" ({members.length})
          </h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="dashboard-members-list">
            {members.map((m) => {
              const c = pickColor(m.id);
              const isOwnerMember = groupInfo.owner_id === m.id;
              return (
                <li key={m.id} className="flex items-center gap-3 rounded-2xl bg-[#F5F6FA] p-3" data-testid={`dashboard-member-${m.id}`}>
                  <div className="w-10 h-10 grid place-items-center rounded-full font-heading font-bold shrink-0" style={{ backgroundColor: c.bg, color: c.text }}>
                    {m.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold leading-tight truncate">
                      {m.name} {m.id === user.id && <span className="text-xs text-[#8A91A8]">(tu)</span>}
                    </p>
                    <p className="text-xs text-[#8A91A8] truncate">{m.email}</p>
                  </div>
                  {isOwnerMember && (
                    <span className="brut-chip" style={{ backgroundColor: "#FFE9C2", color: "#B07512" }}>Admin</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Group actions at the bottom: owner sees delete, members see leave */}
      {groupInfo && (
        <div className="pt-4 mt-2">
          {isOwner ? (
            <button onClick={deleteGroup} className="w-full px-5 py-3.5 rounded-2xl bg-white border border-[#FFD1D6] text-[#FF4D5E] font-semibold hover:bg-[#FFE3E3] inline-flex items-center justify-center gap-2 shadow-[0_2px_8px_rgba(20,32,80,0.04)]" data-testid="dashboard-delete-group">
              <Trash2 className="w-4 h-4" /> Cancella gruppo "{groupInfo.name}"
            </button>
          ) : (
            <button onClick={leaveGroup} className="w-full px-5 py-3.5 rounded-2xl bg-white border border-[#ECEEF5] text-[#1A2A6C] font-semibold hover:bg-[#F5F6FA] inline-flex items-center justify-center gap-2 shadow-[0_2px_8px_rgba(20,32,80,0.04)]" data-testid="dashboard-leave-group">
              <DoorOpen className="w-4 h-4" /> Esci dal gruppo "{groupInfo.name}"
            </button>
          )}
        </div>
      )}
    </div>
  );
}
